import React, {useState, useEffect} from 'react';
import {Button, Modal, Dialogue, addToast, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import type {ClassroomModelV2, GradeLevelsModelV1} from '@albert-io/models';

import GradeSelector from 'client/Classrooms/shared/GradeSelector/GradeSelector.react';

interface PromptGradeLevelModalProps {
  handleResetTimer: () => void;
  handleClearTimer: () => void;
  classroom: ClassroomModelV2;
}

const PromptGradeLevelModal = ({
  handleClearTimer,
  handleResetTimer,
  classroom
}: PromptGradeLevelModalProps) => {
  const [selectedGradeLevels, setSelectedGradeLevels] = useState<GradeLevelsModelV1[]>([]);
  const [allGradeLevels, setAllGradeLevels] = useState<GradeLevelsModelV1[]>([]);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let subscribed = true;
    async function fetchData() {
      try {
        const res = (await resource('grade_levels_v1')
          .mandarkEndpoint(['grade_levels_v1'])
          .getResourcePromise()) as GradeLevelsModelV1[];
        const sortedRes = res.sort((a, b) => {
          return a.getGradeLevelType().localeCompare(b.getGradeLevelType(), undefined, {
            numeric: true,
            sensitivity: 'base'
          });
        });
        if (subscribed) {
          setAllGradeLevels(sortedRes);
        }
      } catch {
        addToast({
          color: 'negative',
          title: 'Error',
          message: 'Something went wrong with your request. Please try again.'
        });
      }
    }
    fetchData();
    return () => {
      subscribed = false;
    };
  }, []);

  const onSave = async () => {
    if (selectedGradeLevels.length === 0) {
      setHasError(true);
    } else {
      const gradeLevelIds = selectedGradeLevels.map((gradeLevel) => gradeLevel.getId());
      try {
        await classroom.updateRelationship('grade_levels_v1', gradeLevelIds).save();
        invalidatePartialInterest({resourcePath: ['classroom_v2', classroom.getId()]});
        addToast({
          color: 'positive',
          title: 'Success!',
          message: `Grade levels added to ${classroom.getName()}`
        });
        handleClearTimer();
      } catch {
        addToast({
          color: 'negative',
          title: 'Error',
          message: 'Something went wrong with your request. Please try again.'
        });
      }
    }
  };

  return (
    <Modal ariaLabel='Create new classroom modal' handleClose={handleResetTimer}>
      {({modalContentStyle}) => {
        return (
          <Dialogue inModal hideCloseBtn paddingLevel={0} className={modalContentStyle}>
            <Dialogue.Body>
              <Text as='div' className='w-full border-b border-subtle p-x2 font-bold'>
                Which grades are taught in {classroom.getName()}?
              </Text>
              <div className='w-full border-b border-subtle px-x2'>
                <GradeSelector
                  setSelectedGradeLevels={(newSelection) => {
                    if (hasError) {
                      setHasError(false);
                    }
                    setSelectedGradeLevels(newSelection);
                  }}
                  selectedGradeLevels={selectedGradeLevels}
                  errorMessage={hasError ? 'Select at least one grade level.' : null}
                  allGradeLevels={allGradeLevels}
                />
              </div>
              <div className='flex justify-end gap-x1 p-x2'>
                <Button onClick={handleResetTimer} variant='outlined'>
                  Skip For Now
                </Button>
                <Button onClick={onSave} color='primary'>
                  Save Changes
                </Button>
              </div>
            </Dialogue.Body>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

export default PromptGradeLevelModal;
